import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles'
import { connect } from 'react-redux';
import { defineMessages, injectIntl, FormattedMessage, FormattedDate } from 'react-intl';
import moment from 'moment';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';

import { colorTeal } from '../../../utils/variables.scss';
import { Default, Mobile } from '../../../utils/mediaQueries';
import studentInfo from '../../../actions/studentInfo';
import {
  makeAppointment,
  cancelAppointment,
  updateAppointment,
  flagNoMatchingTimeslot,
} from '../../../actions/scheduling';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import Checkbox from '../../../components/Checkbox';
import Tooltip from '../../../components/Tooltip';
import { withRtl } from '../../../components/hoc';
import ScheduleButton from '../ScheduleButton';
import messages from '../../../locale/messages';
import { formatTime, formatDate, customisedIntlFormatMessage } from '../../../utils/helpers';
import style from './ScheduleForm.scss'; // eslint-disable-line css-modules/no-unused-class

const STEP_1 = 'assessmentTypes';
const STEP_2 = 'accommodations';
const STEP_3 = 'timeSlots';
const STEP_4 = 'schedule';
const STEP_5 = 'success';
const STEP_BOROUGH = 'borough';
const STEP_LANGUAGE = 'language';
const STEP_LOCATION_SCHOOL = 'locationSchool';
const CANCEL_STEP = 'cancel';
const FURTHER_ACTION_STEP = 'furtherAction';
const NONE_WORK_STEP = 'none_work';

const ANIMATION_DURATION = 700;

/* eslint-disable css-modules/no-undef-class */
const localMessages = defineMessages({
  appointmentCancelButton: {
    id: 'schedule.AppointmentCancelButton',
    defaultMessage: 'Cancel this appointment entirely',
    description: 'The appointment cancellation button text',
  },
  appointmentScheduledTitle: {
    id: 'schedule.AppointmentScheduledTitle',
    defaultMessage: 'Your appointment has been scheduled!',
    description: 'The appointment scheduled title',
  },
  appointmentScheduledSubTitle: {
    id: 'schedule.AppointmentScheduledSubTitle',
    defaultMessage:
      "We added it to your checklist as a reminder and saved this program if you hadn't already. Good luck!",
    description: 'The appointment scheduled subtitle',
  },
  appointmentScheduledText: {
    id: 'schedule.AppointmentScheduledText',
    defaultMessage:
      'Please note: This date is requested, but not yet confirmed. When you attend this assessment, <b>you’ll need to bring your test/audition ticket with you.</b> We’ll send you an email when your ticket(s) are ready to download/print. You will be able to do this yourself or have your schools counselor print for you.',
    description: 'The text shown when the `None of these work for me` button is clicked.',
  },
  appointmentGTScheduledTextP1: {
    id: 'schedule.GTAppointmentScheduledTextP1',
    defaultMessage: `Please note: This date is requested, but not yet confirmed. When you attend this assessment, <b>you’ll need to bring your test appointment letter with you.</b> We'll send you an email when your ticket(s) are ready to download/print.`,
    description: 'The text shown when a g&t appointment is made.',
  },
  appointmentGTScheduledTextP2: {
    id: 'schedule.GTAppointmentScheduledTextp2',
    defaultMessage: `For more information on Gifted & Talented admissions policies please visit our website and read the Gifted & Talented Admissions Guide {link}.`,
    description: 'The text shown when a g&t appointment is made.',
  },
  noneWorkText: {
    id: 'schedule.NoneWorkText',
    defaultMessage:
      'Your registration is complete. The Office of Student Enrollment will confirm your schedule before the test/audition.',
    description: 'The text shown when the `None of these work for me` button is clicked.',
  },
  gtNoneWorkText: {
    id: 'schedule.gtNoneWorkText',
    defaultMessage: 'Your test registration is complete.',
    description: 'The text shown when the `None of these work for me` button is clicked in the g&t proccess.',
  },
  programAccommodationsInfo: {
    id: 'schedule.ProgramAccommodationsInfo',
    defaultMessage:
      'Accommodations may include any instructional accessibility needs or extenuating ' +
      'circumstances that would require accommodating.',
    description: 'The text under Accommodations header',
  },
  programLargePrintAccommodationsBeforeTooltip: {
    id: 'schedule.ProgramLargePrintAccommodationsBeforeTooltip',
    defaultMessage: 'Does {name} have a visual impairment that requires a large print book for assessment?',
    description: 'The text before the large print accommodations tooltip',
  },
  programOtherAccommodationsBeforeTooltip: {
    id: 'schedule.ProgramOtherAccommodationsBeforeTooltip',
    defaultMessage: 'Does {name} require any other testing accommodations?',
    description: 'The text before the other accommodations tooltip',
  },
  program504AccommodationsBeforeTooltip: {
    id: 'schedule.Program504AccommodationsBeforeTooltip',
    defaultMessage: 'Does {name} have a',
    description: 'The text before the 504 accommodations tooltip',
  },
  program504AccommodationsTooltip: {
    id: 'schedule.Program504AccommodationsTooltip',
    defaultMessage:
      'A 504 Plan identifies allergies, injuries, and other factors that may require a testing accommodation.',
    description: 'The text in the 504 accommodations tooltip',
  },
  program504AccommodationsTooltipLabel: {
    id: 'schedule.Program504AccommodationsTooltipLabel',
    defaultMessage: '504 Plan?',
    description: 'The label for the 504 accommodations tooltip',
  },
  programNoAccommodations: {
    id: 'schedule.ProgramNoAccommodations',
    defaultMessage: 'The selected program provides no accommodations.',
    description: "The string shown when a program doesn't have any accommodations",
  },
  programAccommodationsNoIEPELL: {
    id: 'schedule.programAccommodationsNoIEPELL',
    defaultMessage: 'We understand that you have the following accommodations',
    description: 'The string shown if user has no IEP nor ELL accommodations',
  },
  programAccommodationsIEP: {
    id: 'schedule.programAccommodationsIEP',
    defaultMessage:
      'We understand that you have testing accommodations because you have an ' +
      'Individualized Education Program (IEP).',
    description: 'The string shown if user has only IEP accommodations',
  },
  programAccommodationsELL: {
    id: 'schedule.programAccommodationsELL',
    defaultMessage:
      'We understand that you have testing accommodations because you are an ' +
      'English Language Learner or a former English Language Learner who has achieved ' +
      'proficiency on the NYSESLAT within the past two years.',
    description: 'The string shown if user has only ELL accommodations',
  },
  programAccommodationsIEPELL: {
    id: 'schedule.programAccommodationsIEPELL',
    defaultMessage:
      'We understand that you have an Individualized Education Program (IEP) and/or you have ' +
      'testing accommodations because you are an English Language Learner or a former English ' +
      'Language Learner who has achieved proficiency on the NYSESLAT within the past two years.',
    description: 'The string shown if user has both IEP and ELL accommodations',
  },
  programAccommodationsEmptyTextTop: {
    id: 'schedule.ProgramAccommodationsEmptyTextTop',
    defaultMessage: 'We do not have any accommodation requirements on file for your child.',
    description: 'The program accommodations empty top text',
  },
  programAccommodationsTextTop: {
    id: 'schedule.ProgramAccommodationsTextTop',
    defaultMessage: 'The following accommodations are available to you:',
    description: 'The program accommodations top text',
  },
  programAccommodationsTextBottom: {
    id: 'schedule.ProgramAccommodationsTextBottom',
    defaultMessage:
      "If this information is incorrect or your child has a 504 Plan, contact your child's school counselor.",
    description: 'The program accommodations bottom text',
  },
  programAccommodationsEmptyTitle: {
    id: 'schedule.ProgramAccommodationsEmptyTitle',
    defaultMessage: 'With no approved accommodations',
    description: 'The empty program accommodations title',
  },
  programAccommodationsTitle: {
    id: 'schedule.ProgramAccommodationsTitle',
    defaultMessage: 'With use of {accommodations}',
    description: 'The program accommodations title',
  },
  programAccommodationsTitleSelected: {
    id: 'schedule.ProgramAccommodationsTitleSelected',
    defaultMessage: 'Which accommodations would you like to audition for?',
    description: 'The program accommodations title when the accommodations step is selected',
  },
  programAccommodationsVerified504: {
    id: 'schedule.ProgramAccommodationsVerified504',
    defaultMessage: 'You have a verified 504 Plan.',
    description: 'The program accommodations 504 verified plan message',
  },
  programNoAssessments: {
    id: 'schedule.ProgramNoAssessments',
    defaultMessage: 'The selected program provides no assessments.',
    description: "The string shown when a program doesn't have any assessments",
  },
  programAssessmentsTitle: {
    id: 'schedule.ProgramAssessmentsTitle',
    defaultMessage: 'Register for {assessments}',
    description: 'The program assessments title',
  },
  programAssessmentsTitleSelected: {
    id: 'schedule.ProgramAssessmentsTitleSelected',
    defaultMessage: 'Which assessments would you like to audition for?',
    description: 'The program assessments title when the assessments step is selected',
  },
  programAssessmentsTitleSpecial: {
    id: 'schedule.ProgramAssessmentsTitleSpecial',
    defaultMessage: 'Registering for the {title}',
    description: 'The program assessments title when the admission process is SHS',
  },
  programNoTimeSlots: {
    id: 'schedule.ProgramNoTimeSlots',
    defaultMessage:
      'The Office of Student Enrollment will schedule this for you. If you have a 504 plan, your school will submit it to the DOE to review. If approved, you can return here to view the test date assigned for you.',
    description: 'The string shown when no time slots are available',
  },
  programTimeSlotsText: {
    id: 'schedule.ProgramTimeSlotsText',
    defaultMessage: 'Here are some times for you to choose from; please select the option that works best for you.',
    description: 'The program time slots text',
  },
  programTimeSlotsTitle: {
    id: 'schedule.ProgramTimeSlotsTitle',
    defaultMessage: 'on {datetime} at <b>{schoolName}</b>, {location}',
    description: 'The program time slots title',
  },
  programTimeSlotsTitleSelected: {
    id: 'schedule.ProgramTimeSlotsTitleSelected',
    defaultMessage: 'Choose a time',
    description: 'The program time slots title when the time slot step is selected',
  },
  programTimeSlotsNoneWork: {
    id: 'schedule.ProgramTimeSlotsNoneWork',
    defaultMessage: 'Please contact me to find a time',
    description: 'The none of these wok for me button message',
  },
  programTimeSlotsLocation: {
    id: 'schedule.ProgramTimeSlotsLocation',
    defaultMessage: 'This appointment will be located at',
    description: 'The program time slots location string',
  },
  programTimeSlotsLocationTitle: {
    id: 'schedule.ProgramTimeSlotsLocationTitle',
    defaultMessage: 'The following options will be located at',
    description: 'The program time slots location title string',
  },
  programTimeSlotsNoLocationTitle: {
    id: 'schedule.ProgramTimeSlotsNoLocationTitle',
    defaultMessage: 'The following options will be located at a location that is still to be determined.',
    description: 'The program time slots no location title string',
  },
  programReScheduleTitle: {
    id: 'schedule.ProgramReScheduleTitle',
    defaultMessage: 'Want to reschedule?',
    description: 'The program re-schedule title',
  },
  programReScheduleSubTitle: {
    id: 'schedule.ProgramReScheduleSubTitle',
    defaultMessage: 'Click to edit any info below.',
    description: 'The program re-schedule subtitle',
  },
  programReScheduleUpdateSelection: {
    id: 'schedule.ProgramReScheduleUpdateSelection',
    defaultMessage: '* in order to be able to reschedule, please update your selection',
    description: 'The program re-schedule update selection text',
  },
  programScheduleTitle: {
    id: 'schedule.ProgramScheduleTitle',
    defaultMessage: 'Looks good!',
    description: 'The program schedule title',
  },
  programScheduleSubTitle: {
    id: 'schedule.ProgramScheduleSubTitle',
    defaultMessage: 'You can edit anything here before you click `Submit Registration` to request this time.',
    description: 'The program schedule subtitle',
  },
  programScheduleCancelTitle: {
    id: 'schedule.ProgramScheduleCancelTitle',
    defaultMessage: 'Are you sure you want to cancel?',
    description: 'The program schedule cancellation title',
  },
  programScheduleSHSTestName: {
    id: 'schedule.ProgramScheduleSHSTestName',
    defaultMessage: 'Specialized High School Admissions Test',
    description: 'The SHS test name',
  },
  timeSlotConflict: {
    id: 'schedule.TimeSlotConflict',
    defaultMessage: '{admissionMethod} for {assessments} at {school}',
    description: 'The time slot conflict text',
  },
  gtNoTimeSlotsCurrentStudents: {
    id: 'schedule.gtNoTimeSlotsCurrentStudents',
    defaultMessage: `Current NYC public school students in grades K-2 (not including charter schools) will be tested at their school during the weekday. Please contact your school to inquire about exact testing dates.`,
    description: 'Message for current nyc students with no g&t time slots',
  },
  gtNoTimeSlotsNotCurrentStudents: {
    id: 'schedule.gtNoTimeSlotsNotCurrentStudents',
    defaultMessage: `Current Pre-Kindergarten, Charter School, and Non-Public School Students will be contacted by the Office of Assessment to schedule a weekend testing appointment in mid-December.`,
    description: 'Message for noncurrent nyc students with no g&t time slots',
  },
  gtNoTimeSlotsClickNextInstruction: {
    id: 'schedule.gtNoTimeSlotsClickNextInstruction',
    defaultMessage: `Click “NEXT” below to complete your request for test scheduling.`,
    description: 'Instruction to click next',
  },
  gtSelectBorough: {
    id: 'schedule.gtSelectBorough',
    defaultMessage: `Select Borough`,
    description: 'Instruction to select a borough',
  },
  gtBoroughSelected: {
    id: 'schedule.gtBoroughSelected',
    defaultMessage: `Borough Selected:`,
    description: 'Label for selected borough',
  },
  gtLanguageTitle: {
    id: 'schedule.gtLanguageTitle',
    defaultMessage: `This exam is offered in 11 languages. Which language would your child prefer to take this exam in?`,
    description: 'Title for G&T language accordion',
  },
  gtSelectLanguage: {
    id: 'schedule.gtSelectLanguage',
    defaultMessage: `Select Language`,
    description: 'Instruction to select a language',
  },
  gtLanguageSelected: {
    id: 'schedule.gtLanguageSelected',
    defaultMessage: `Language Selected:`,
    description: 'Label for selected language',
  },
  gtSelectTestingLocation: {
    id: 'schedule.gtSelectTestingLocation',
    defaultMessage: `Select Testing Location`,
    description: 'Instruction to select a testing location',
  },
  gtTestingLocationSelected: {
    id: 'schedule.gtTestingLocationSelected',
    defaultMessage: `Testing Location:`,
    description: 'Label for selected testing location',
  },
  gtAccommodationsRequested: {
    id: 'schedule.gtAccommodationsRequested',
    defaultMessage: `With Accommodations Requested`,
    description: 'Label for accomodations when some are requested',
  },
  gtNoAccommodationsRequested: {
    id: 'schedule.gtNoAccommodationsRequested',
    defaultMessage: `With No Accommodations Requested`,
    description: 'Label for accomodations when none are requested',
  },
  programScheduleGTTestName: {
    id: 'schedule.programScheduleGTTestName',
    defaultMessage: 'Gifted and Talented Exam',
    description: 'The SHS test name',
  },
});

class ScheduleForm extends React.PureComponent {
  constructor(props) {
    super(props);

    this.appointmentData = this.getAppointmentData();

    this.initialState = {
      currentStep: STEP_1,
      selectedAssessmentTypes: this.appointmentData.assessmentTypes,
      accommodation504: props.user.details.data.acc_flag_504,
      accommodationLargePrint: false,
      accommodationOther: false,
      selectedTimeSlot: this.appointmentData.timeSlot,
      assessmentStepPassed: false,
      accommodationStepPassed: false,
      timeSlotStepPassed: false,
      locationStepPassed: false,
      boroughStepPassed: false,
      languageStepPassed: false,
      reSchedule: false,
      filterBorough: null,
      filterLanguage: null,
      filterLocation: null,
      filterLocationName: null,
      filtersChanged: false,
      ...this.getInitialData(),
    };

    this.state = { ...this.initialState };

    this.stepMapping = {
      [STEP_1]: this.renderFormStep1,
      [STEP_2]: this.renderFormStep2,
      [STEP_3]: this.renderFormStep3,
    };

    this.stepMappingLGA = {
      [STEP_1]: this.renderFormStep1,
      [STEP_2]: this.renderFormStep3,
      // [STEP_3]: this.renderFormStep3,
    };

    this.stepMappingGTNoTimeSlots = {
      [STEP_1]: this.renderFormStep1,
      [STEP_2]: this.renderFormStep2,
      [STEP_LANGUAGE]: this.renderGTFormStepLanguage,
      [STEP_BOROUGH]: this.renderFormStepBorough,
      [STEP_LOCATION_SCHOOL]: this.renderGTFormStepLocationSchool,
    };

    this.stepMappingGTTimeSlots = {
      [STEP_1]: this.renderFormStep1,
      [STEP_2]: this.renderFormStep2,
      [STEP_LANGUAGE]: this.renderGTFormStepLanguage,
      [STEP_BOROUGH]: this.renderFormStepBorough,
      [STEP_LOCATION_SCHOOL]: this.renderGTFormStepLocationSchool,
      [STEP_3]: this.renderFormStep3,
    };

    this.stepTitleMapping = {
      [STEP_4]: this.renderFormStep4Title,
      [STEP_5]: this.renderFormStep5Title,
      [CANCEL_STEP]: this.renderFormCancelStepTitle,
      [FURTHER_ACTION_STEP]: this.renderFurtherActionStepTitle,
    };

    this.assessmentTypes = this.getAssessmentTypesForProgram(props.program);
    this.accommodations = props.user.details.data.accommodations;
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.program !== nextProps.program && !this.props.isGT) {
      this.setState({ ...this.initialState });
    }

    if (
      (!this.props.appointmentForm.result.id && nextProps.appointmentForm.result.id) ||
      (!this.props.updateAppointmentForm.result.id && nextProps.updateAppointmentForm.result.id)
    ) {
      this.setState({ ...this.state, currentStep: STEP_5 });
      this.props.focusModal();
      this.props.studentInfo(this.props.user.applicationId);
    }

    if (this.props.user.details.data.acc_flag_504 !== nextProps.user.details.data.acc_flag_504) {
      this.setState({ ...this.state, accommodation504: nextProps.user.details.data.acc_flag_504 });
    }
  }

  getInitialData = () => {
    const { program, isGT } = this.props;

    if (program.appointment) {
      let data = {
        assessmentStepPassed: true,
        accommodationStepPassed: true,
        timeSlotStepPassed: true,
        reSchedule: true,
        currentStep: STEP_4,
      };

      if (isGT && program.appointment.time_slot) {
        data = {
          ...data,
          accommodationLargePrint: program.appointment.accommodation_large_print,
          accommodationOther: program.appointment.accommodation_other,
          filterBorough: program.appointment.time_slot.location_school.district.borough,
          filterLanguage: program.appointment.time_slot.assessment_types[0].name,
          filterLocation: program.appointment.time_slot.location_school.dbn,
          filterLocationName: program.appointment.time_slot.location_school.name,
          timeSlotStepPassed: true,
          locationStepPassed: true,
          boroughStepPassed: true,
          languageStepPassed: true,
          currentStep: STEP_4,
        };
        this.props.getGTfiltered({
          assessment_types: [program.appointment.time_slot.assessment_types[0].name],
          borough: program.appointment.time_slot.location_school.district.borough,
          location_school_dbn: program.appointment.time_slot.location_school.dbn,
        });
      }
      return data;
    }

    if (this.skipAssessments()) {
      return {
        assessmentStepPassed: true,
        currentStep: STEP_2,
      };
    }

    return {};
  };

  getAppointmentData = () => {
    const { program } = this.props;
    let data = { assessmentTypes: [], accommodations: [], accommodation504: false, timeSlot: null };

    if (program.appointment) {
      data = {
        assessmentTypes: program.appointment.assessment_types.map(id => `${id}`),
        accommodation504: program.appointment.accommodation_504,
        timeSlot: `${program.appointment.time_slot.id}`,
        accommodationLargePrint: program.appointment.accommodation_large_print,
        accommodationOther: program.appointment.accommodation_other,
      };
    }

    return data;
  };

  getAssessmentTypesForProgram = program => {
    const assessmentTypeIds = {};
    const assessmentTypes = [];

    if (program.schedule) {
      program.schedule.time_slots.forEach(timeSlot => {
        timeSlot.assessment_types.forEach(assessmentType => {
          if (!assessmentTypeIds[assessmentType.id]) {
            assessmentTypeIds[assessmentType.id] = true;
            assessmentTypes.push(assessmentType);
          }
        });
      });
    }

    return assessmentTypes;
  };

  getAppointmentTimeRanges = () => {
    const { studentAppointments } = this.props;

    const ranges = [];

    studentAppointments.forEach(appointment => {
      const start = moment(appointment.time_slot.start_at);
      const end = moment(appointment.time_slot.end_at);
      ranges.push({ appointment, start, end });
    });

    return ranges;
  };

  getConflictingAppointment = (timeSlot, existingRanges = this.getAppointmentTimeRanges()) => {
    const start = moment(timeSlot.start_at);
    const end = moment(timeSlot.end_at);

    const sameScheduleAppointmentIds = existingRanges
      .filter(
        ({ appointment }) =>
          (timeSlot.schedule_id && appointment.time_slot.schedule_id === timeSlot.schedule_id) ||
          (timeSlot.shared_schedule_id && appointment.time_slot.shared_schedule_id === timeSlot.shared_schedule_id),
      )
      .map(({ appointment }) => appointment.id);

    const range = existingRanges
      .filter(({ appointment }) => !sameScheduleAppointmentIds.includes(appointment.id))
      .find(r => start.isSameOrBefore(r.end) && end.isSameOrAfter(r.start));

    return range ? range.appointment : null;
  };

  getFilteredTimeSlots = (
    { selectedAssessmentTypes = this.state.selectedAssessmentTypes, accommodation504 = this.state.accommodation504 } = {
      selectedAssessmentTypes: this.state.selectedAssessmentTypes,
      accommodation504: this.state.accommodation504,
    },
  ) => {
    const { program, user } = this.props;
    const timeSlots = [];

    const ranges = this.getAppointmentTimeRanges();
    const skipAssessments = this.skipAssessments();

    if (program.schedule) {
      program.schedule.time_slots.forEach(timeSlot => {
        if (!user.isGT) {
          const availableTypes = timeSlot.assessment_types.map(at => `${at.id}`);
          // every selected type needs to be defined in a time slot in order for the time slot to be valid
          const selectedATsNotInTimeSlot = selectedAssessmentTypes.filter(atId => !availableTypes.includes(atId));

          const assessmentsOk =
            skipAssessments ||
            (selectedAssessmentTypes.length > 0 &&
              selectedATsNotInTimeSlot.length === 0 &&
              (!timeSlot.single_assessment_type || selectedAssessmentTypes.length === 1));

          // either the time slot supports accommodations, or the user has no accommodations
          const accommodationsOk = timeSlot.accommodations || this.accommodations.length === 0;

          const accommodation504ok =
            !(accommodation504 || user.details.data.has_verified_504_plan) || timeSlot.accommodation_504;

          if (assessmentsOk && accommodationsOk && accommodation504ok) {
            // also get any conflicting time slots
            timeSlots.push({ ...timeSlot, conflict: this.getConflictingAppointment(timeSlot, ranges) });
          }
        }
        if (user.isGT) {
          timeSlots.push({ ...timeSlot, conflict: this.getConflictingAppointment(timeSlot, ranges) });
        }
      });
    }

    return timeSlots;
  };

  getTimeSlotsByLocation = timeSlots => {
    // also return all locations for easier iteration
    const usedLocations = [];
    const timeSlotsByLocation = { locations: [], timeSlots: {}, timeSlotsNoLocation: [] };
    timeSlots
      .filter(ts => ts.school)
      .forEach(timeSlot => {
        const locationDbn = timeSlot.school.dbn;
        if (!usedLocations.includes(locationDbn)) {
          timeSlotsByLocation.timeSlots[locationDbn] = [];
          timeSlotsByLocation.locations.push(timeSlot.school);
          usedLocations.push(locationDbn);
        }
        timeSlotsByLocation.timeSlots[locationDbn].push(timeSlot);
      });
    timeSlots
      .filter(ts => ts.school === null)
      .forEach(timeSlot => {
        timeSlotsByLocation.timeSlotsNoLocation.push(timeSlot);
      });
    return timeSlotsByLocation;
  };

  getTimeSlotsByDates = timeSlots => {
    // also return all dates in an array so sorting can be used
    const timeSlotsByDates = { dates: [], timeSlots: {} };
    timeSlots.forEach(timeSlot => {
      const date = moment(timeSlot.start_at).format('Y-MM-DD');
      if (!timeSlotsByDates.dates.includes(date)) {
        timeSlotsByDates.timeSlots[date] = [];
        timeSlotsByDates.dates.push(date);
      }
      timeSlotsByDates.timeSlots[date].push(timeSlot);
    });
    return timeSlotsByDates;
  };

  getStepButtonClasses = step => {
    const stepButtonClasses = ['button--no-style', style.scheduleFormStepButton];

    if (this.isCurrentStep(step)) {
      stepButtonClasses.push(style.scheduleFormStepButtonActive);
    } else if (this.isCurrentStep(STEP_5) || this.isCurrentStep(CANCEL_STEP)) {
      stepButtonClasses.push(style.scheduleFormStepButtonDone);
    }

    return { stepButtonClasses };
  };

  schedule = () => {
    this.props.makeAppointment(this.props.applicationId, {
      time_slot: this.state.selectedTimeSlot,
      student_application: this.props.applicationId,
      // assessment_types: this.state.selectedAssessmentTypes,
      // suppressing for G&T, don't have id's needed for saving
      assessment_types: [],
      accommodation_504: this.state.accommodation504,
      accommodation_large_print: this.state.accommodationLargePrint,
      accommodation_other: this.state.accommodationOther,
    });
  };

  cancelAppointment = () => {
    const { appointment } = this.props.program;
    if (appointment) {
      this.props.cancelAppointment(this.props.applicationId, appointment.id);
    }
  };

  reSchedule = () => {
    const { appointment } = this.props.program;
    if (appointment) {
      this.props.updateAppointment(this.props.applicationId, {
        appointment_id: appointment.id,
        time_slot: this.state.selectedTimeSlot,
        student_application: this.props.applicationId,
        // assessment_types: this.state.selectedAssessmentTypes,
        // suppressing for G&T, don't have id's needed for saving
        assessment_types: [],
        accommodation_504: this.state.accommodation504,
        accommodation_large_print: this.state.accommodationLargePrint,
        accommodation_other: this.state.accommodationOther,
      });
    }
  };

  reScheduleDataChanged = () => {
    const { selectedAssessmentTypes, accommodation504, selectedTimeSlot } = this.state;

    const assessmentsChanged =
      this.appointmentData.assessmentTypes.length !== selectedAssessmentTypes.length ||
      this.appointmentData.assessmentTypes.filter(id => !selectedAssessmentTypes.includes(id)).length > 0;

    const accommodation504Changed = this.appointmentData.accommodation504 !== accommodation504;

    const timeSlotChanged = this.appointmentData.timeSlot !== selectedTimeSlot;

    return assessmentsChanged || accommodation504Changed || timeSlotChanged;
  };

  availableTimeSlot = (filters = {}, timeSlot = this.state.selectedTimeSlot) =>
    this.getFilteredTimeSlots(filters)
      .map(ts => `${ts.id}`)
      .includes(timeSlot)
      ? timeSlot
      : null;

  assessmentChanged = event => {
    const { selectedAssessmentTypes } = this.state;
    let newSelectedAssessmentTypes = null;

    if (event.target.checked && !selectedAssessmentTypes.includes(event.target.value)) {
      newSelectedAssessmentTypes = [...selectedAssessmentTypes, event.target.value];
    } else if (!event.target.checked) {
      const filteredOut = selectedAssessmentTypes.filter(assessmentTypeId => assessmentTypeId !== event.target.value);
      newSelectedAssessmentTypes = [...filteredOut];
    }

    if (newSelectedAssessmentTypes === null) {
      return;
    }

    this.setState({
      ...this.state,
      selectedAssessmentTypes: newSelectedAssessmentTypes,
      selectedTimeSlot: this.availableTimeSlot({ selectedAssessmentTypes: newSelectedAssessmentTypes }),
    });
  };

  accommodation504Changed = event => {
    this.setState({
      ...this.state,
      accommodation504: event.target.checked,
      selectedTimeSlot: this.availableTimeSlot({ accommodation504: event.target.checked }),
    });
  };

  accommodationLargePrintChanged = event => {
    this.setState({
      ...this.state,
      accommodationLargePrint: event.target.value === 'true',
    });
  };

  accommodationOtherChanged = event => {
    this.setState({
      ...this.state,
      accommodationOther: event.target.value === 'true',
    });
  };

  filterLocation = event => {
    if (event.target.value === this.state.filterBorough) {
      this.setState({
        ...this.state,
        filterLocation: null,
        filterLocationName: null,
      });
    }
    this.setState({
      ...this.state,
      filterLocation: event.target.value,
      filterLocationName: event.target.schoolName,
    });
  };

  filterBorough = event => {
    if (event.target.value === this.state.filterBorough) {
      this.setState({
        ...this.state,
        filterBorough: null,
        filtersChanged: true,
      });
    }
    this.setState({
      ...this.state,
      filterBorough: event.target.value,
      filtersChanged: true,
    });
  };

  filterLanguage = event => {
    this.setState({
      ...this.state,
      filterLanguage: event.target.value,
      selectedAssessmentTypes: [event.target.value],
      filtersChanged: true,
    });
    if (event.target.value === this.state.filterLanguage) {
      this.setState({
        ...this.state,
        filterLanguage: null,
        selectedAssessmentTypes: [],
        filtersChanged: true,
      });
    }
  };

  timeSlotChanged = event => {
    let timeSlotId = event.target.value;
    if (!event.target.checked) {
      timeSlotId = null;
    }

    this.setState({ ...this.state, selectedTimeSlot: timeSlotId });
  };

  assessmentSelected = () => this.skipAssessments() || this.state.selectedAssessmentTypes.length > 0;

  assessmentPassed = () => this.state.assessmentStepPassed;

  // TODO: Improve assesment types length check
  skipAssessments = () =>
    this.props.skipAssessments || this.getAssessmentTypesForProgram(this.props.program).length === 0;

  accommodationPassed = () => this.state.accommodationStepPassed;

  timeSlotSelected = () => this.state.selectedTimeSlot !== null;

  timeSlotPassed = () => this.state.timeSlotStepPassed;

  isCurrentStep = step => this.state.currentStep === step;

  allStepsPassed = () => this.state.currentStep === STEP_5 || this.state.currentStep === CANCEL_STEP;

  checkLocationsLoaded = ({ show, accommodationGT, buttons }) => {
    const { locations, intl, user } = this.props;
    const { is_gt_timeslots_eligible: hasGTTimeSlots } = user.details.data;

    if (
      !this.props.gtFilteredLocationsLoading &&
      (!this.props.gtFilteredLocationsLoaded || this.state.filtersChanged) &&
      this.state.filterBorough &&
      this.state.filterLanguage
    ) {
      this.setState({
        ...this.state,
        filtersChanged: false,
      });
      this.props.getGTfilteredLocations({
        assessment_types: this.state.filterLanguage,
        borough: this.state.filterBorough,
      });
    } else if (this.props.gtFilteredLocationsLoading) {
      return <Loader />;
    }
    if (!locations.length || !hasGTTimeSlots) {
      return (
        <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
          <div className={style.scheduleFormStepWrapper}>
            <div className={style.scheduleFormStep}>
              <div>
                <div className={style.scheduleFormTimeSlots}>
                  <p>
                    <FormattedMessage {...localMessages.gtNoTimeSlotsCurrentStudents} />
                  </p>
                  <p>
                    <FormattedMessage {...localMessages.gtNoTimeSlotsNotCurrentStudents} />
                  </p>
                </div>
                <Button
                  label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
                  onClick={() => {
                    this.props.flagNoMatchingTimeslot(
                      this.props.user.applicationId,
                      {
                        student_application_id: this.props.applicationId,
                        schedule_id: this.props.program.schedule.programs ? null : this.props.program.schedule.id,
                        shared_schedule_id: this.props.program.schedule.programs
                          ? this.props.program.schedule.id
                          : null,
                        accommodation_large_print: this.state.accommodationLargePrint,
                        accommodation_other: this.state.accommodationOther,
                        language_requested: this.state.selectedAssessmentTypes[0],
                      },
                      this.state.accommodation504,
                    );
                    this.setState({ ...this.state, currentStep: NONE_WORK_STEP });
                    this.props.focusModal();
                  }}
                  iconColor={colorTeal}
                />
              </div>
            </div>
          </div>
        </AnimateHeight>
      );
    }

    return (
      <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
        <div className={style.scheduleFormStepWrapper}>
          <div className={style.scheduleFormStep}>
            <div>
              {accommodationGT}
              {buttons}
            </div>
          </div>
        </div>
      </AnimateHeight>
    );
  };

  noLocationsCheck = () => {
    if (this.props.isGT) {
      return (
        <Fragment>
          <p>
            <FormattedMessage {...localMessages.gtNoTimeSlotsCurrentStudents} />
          </p>
          <p>
            <FormattedMessage {...localMessages.gtNoTimeSlotsNotCurrentStudents} />
          </p>
          <p>
            <FormattedMessage {...localMessages.gtNoTimeSlotsClickNextInstruction} />
          </p>
        </Fragment>
      );
    }

    return <FormattedMessage {...localMessages.programTimeSlotsNoLocationTitle} />;
  };

  // Duplicating compareByTime function in helpers.js with object property specified
  sortTimeSlotByTime = (firstTime, secondTime) => {
    if (moment(firstTime.start_at).isBefore(moment(secondTime.start_at))) {
      return -1;
    } else if (moment(firstTime.start_at).isAfter(moment(secondTime.start_at))) {
      return 1;
    }
    return 0;
  };

  renderFormStep1Title = () => {
    const { intl, isGT } = this.props;
    const { stepButtonClasses } = this.getStepButtonClasses(STEP_1);
    const isCurrentStep = this.isCurrentStep(STEP_1);

    let title = <FormattedMessage {...localMessages.programAssessmentsTitleSelected} />;

    if (!isCurrentStep) {
      let message = localMessages.programAssessmentsTitle;
      let values = {};
      if (this.skipAssessments()) {
        message = localMessages.programAssessmentsTitleSpecial;
        if (isGT) {
          values = { title: customisedIntlFormatMessage(intl, localMessages.programScheduleGTTestName) };
        } else {
          values = { title: customisedIntlFormatMessage(intl, localMessages.programScheduleSHSTestName) };
        }
      } else {
        const { selectedAssessmentTypes } = this.state;
        values = {
          assessments: this.assessmentTypes
            .filter(at => selectedAssessmentTypes.includes(`${at.id}`))
            .map(at => at.name.toLowerCase())
            .join(', '),
        };
      }

      title = <FormattedMessage {...message} values={values} />;
    }

    return (
      <div className={style.scheduleFormStepTitleContainer}>
        <button
          className={classNames(...stepButtonClasses)}
          onClick={() => this.setState({ ...this.state, currentStep: STEP_1 })}
          aria-expanded={isCurrentStep}
          disabled={isCurrentStep || this.skipAssessments() || this.allStepsPassed()}
        >
          <span className={style.scheduleFormStepButtonIcon}>
            {!isCurrentStep && this.assessmentSelected() && this.assessmentPassed() ? (
              <i className="icon-Check" />
            ) : (
              <i className="icon-Pencil" />
            )}
          </span>
          {isCurrentStep ? (
            <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
          ) : (
            <span className={style.scheduleFormStepTitle}>{title}</span>
          )}
        </button>
      </div>
    );
  };

  renderFormStep1Body = () => {
    if (this.skipAssessments()) {
      return <Fragment />;
    }

    const { intl } = this.props;

    let assessmentsContent = (
      <div className={style.scheduleFormAssessmentsEmpty}>
        <FormattedMessage {...localMessages.programNoAssessments} />
      </div>
    );

    if (this.assessmentTypes.length > 0) {
      assessmentsContent = (
        <fieldset className={style.scheduleFormCheckboxes}>
          {this.assessmentTypes.map(assessmentType => {
            const key = `assessmentType-${assessmentType.id}`;
            return (
              <Checkbox
                key={key}
                id={key}
                value={assessmentType.id}
                checked={this.state.selectedAssessmentTypes.includes(`${assessmentType.id}`)}
                label={assessmentType.name}
                onChange={this.assessmentChanged}
              />
            );
          })}
        </fieldset>
      );
    }

    const assessmentSelected = this.assessmentSelected();

    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => this.setState({ ...this.state, currentStep: STEP_2, assessmentStepPassed: true })}
          disabled={!assessmentSelected}
        />
      </div>
    );

    return (
      <div className={style.scheduleFormStepWrapper}>
        <div className={style.scheduleFormStep}>
          {assessmentsContent}
          {buttons}
        </div>
      </div>
    );
  };

  renderFormStep1 = (show = false) => (
    <div key="step1" className={style.scheduleFormStepContainer}>
      {this.renderFormStep1Title()}
      <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
        {this.renderFormStep1Body()}
      </AnimateHeight>
    </div>
  );

  renderFormStep2 = (show = false) => {
    const { user, intl } = this.props;

    const accommodationsString = this.accommodations.map(accommodation => accommodation.name).join(', ');
    const accommodationList = this.accommodations.map(accommodation => (
      <li key={accommodation.name}>{accommodation.name}</li>
    ));

    const userDetails = this.props.user.details.data;
    const accommodationIEP = userDetails.acc_iep;
    const accommodationELL = userDetails.acc_ell;
    const accommodationOther = userDetails.acc_other;
    const learnMoreLink =
      'http://schools.nyc.gov/Academics/SpecialEducation/FamilyResources/GuidesDocuments/default.htm';

    if (accommodationOther) {
      accommodationList.push(<li key={accommodationOther}>{accommodationOther}</li>);
    }

    const accommodationsContent = (
      <div className={style.scheduleFormAccommodationsEmpty}>
        <div className={style.scheduleFormAccommodationsText}>
          {accommodationList.length > 0 ? (
            <Fragment>
              <p>
                {accommodationIEP && accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsIEPELL} />
                )}
                {accommodationIEP && !accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsIEP} />
                )}
                {!accommodationIEP && accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsELL} />
                )}
                {!accommodationIEP && !accommodationELL && (
                  <FormattedMessage {...localMessages.programAccommodationsNoIEPELL} />
                )}
                <a
                  href={learnMoreLink}
                  target="_blank"
                  rel="noopener"
                  className={style.scheduleFormLearnMore}
                  aria-label={`${customisedIntlFormatMessage(intl, messages.learnMoreLink)} ${customisedIntlFormatMessage(intl, 
                    messages.opensInNewWindow,
                  )}`}
                >
                  <FormattedMessage {...messages.learnMoreLink} />
                </a>
              </p>
              {(accommodationIEP || accommodationELL) && (
                <p>
                  <FormattedMessage {...localMessages.programAccommodationsTextTop} />
                </p>
              )}
              <ul>{accommodationList}</ul>
            </Fragment>
          ) : (
            <Fragment>
              <p>
                <FormattedMessage {...localMessages.programAccommodationsInfo} />
              </p>
              <p>
                <FormattedMessage {...localMessages.programAccommodationsEmptyTextTop} />
              </p>
            </Fragment>
          )}
        </div>
        {user.details.data.has_verified_504_plan && (
          <div
            className={classNames(
              style.scheduleFormAccommodationsText,
              style.scheduleFormAccommodationsVerified504,
            )}
          >
            <FormattedMessage {...localMessages.programAccommodationsVerified504} />
          </div>
        )}
        <div className={style.scheduleFormAccommodationsText}>
          <FormattedMessage {...localMessages.programAccommodationsTextBottom} />
        </div>
      </div>
    );

    let accommodationGT;
    if (user.isGT) {
      accommodationGT = (
        <div className={style.scheduleForm504Accommodation}>
          <p>
            <FormattedMessage {...localMessages.programAccommodationsInfo} />
          </p>
          <div className={style.scheduleForm504AccommodationInline}>
            <FormattedMessage
              {...localMessages.programLargePrintAccommodationsBeforeTooltip}
              values={{ name: user.firstName }}
            />
          </div>{' '}
          <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
            <Checkbox
              key="accommodationLargePrint-y"
              id="accommodationLargePrint-y"
              value="true"
              checked={this.state.accommodationLargePrint}
              label="Yes"
              onChange={this.accommodationLargePrintChanged}
            />
            <Checkbox
              key="accommodationLargePrint-n"
              id="accommodationLargePrint-n"
              value="false"
              checked={!this.state.accommodationLargePrint}
              label="No"
              onChange={this.accommodationLargePrintChanged}
            />
          </div>
          <div className={style.scheduleForm504AccommodationInline}>
            <FormattedMessage
              {...localMessages.programOtherAccommodationsBeforeTooltip}
              values={{ name: user.firstName }}
            />
          </div>{' '}
          <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
            <Checkbox
              key="accommodationOther-y"
              id="accommodationOther-y"
              value="true"
              checked={this.state.accommodationOther}
              label="Yes"
              onChange={this.accommodationOtherChanged}
            />
            <Checkbox
              key="accommodationOther-n"
              id="accommodationOther-n"
              value="false"
              checked={!this.state.accommodationOther}
              label="No"
              onChange={this.accommodationOtherChanged}
            />
          </div>
        </div>
      );
    }

    let accommodation504;

    if (!user.details.data.has_verified_504_plan) {
      accommodation504 = (
        <div className={style.scheduleForm504Accommodation}>
          <div className={style.scheduleForm504AccommodationInline}>
            <FormattedMessage
              {...localMessages.program504AccommodationsBeforeTooltip}
              values={{ name: user.firstName }}
            />
          </div>{' '}
          <div
            className={classNames(
              style.scheduleForm504AccommodationInline,
              style.scheduleForm504AccommodationTooltip,
            )}
          >
            <Default>
              <Tooltip icon="" label={customisedIntlFormatMessage(intl, localMessages.program504AccommodationsTooltipLabel)}>
                <FormattedMessage {...localMessages.program504AccommodationsTooltip} />
              </Tooltip>
            </Default>
            <Mobile>
              <Tooltip
                icon=""
                left="-20px"
                top="-120px"
                verticalPosition="top"
                label={customisedIntlFormatMessage(intl, localMessages.program504AccommodationsTooltipLabel)}
              >
                <FormattedMessage {...localMessages.program504AccommodationsTooltip} />
              </Tooltip>
            </Mobile>
          </div>
          <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
            <Checkbox
              key="accommodation504"
              id="accommodation504"
              value="accommodation504"
              checked={this.state.accommodation504}
              label="Yes"
              onChange={this.accommodation504Changed}
            />
          </div>
        </div>
      );
    }

    const nextStep = user.isGT ? STEP_LANGUAGE : STEP_3;

    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => {
            this.setState({ ...this.state, currentStep: nextStep, accommodationStepPassed: true });
            document.getElementById('step3-title-container').focus();
          }}
        />
      </div>
    );

    const { stepButtonClasses } = this.getStepButtonClasses(STEP_2);

    let title = 'Accommodations';

    const isCurrentStep = this.isCurrentStep(STEP_2);

    if (!isCurrentStep) {
      if (this.accommodations.length > 0 || this.state.accommodationOther || this.state.accommodationLargePrint) {
        if (this.props.isGT) {
          title = <FormattedMessage {...localMessages.gtAccommodationsRequested} />;
        } else {
          title = (
            <FormattedMessage
              {...localMessages.programAccommodationsTitle}
              values={{ accommodations: accommodationsString }}
            />
          );
        }
      } else if (this.props.isGT) {
        title = <FormattedMessage {...localMessages.gtNoAccommodationsRequested} />;
      } else {
        title = <FormattedMessage {...localMessages.programAccommodationsEmptyTitle} />;
      }
    }

    return (
      <div key="step2" className={style.scheduleFormStepContainer}>
        <div className={style.scheduleFormStepTitleContainer}>
          <button
            className={classNames(...stepButtonClasses)}
            aria-expanded={isCurrentStep}
            onClick={() => this.setState({ ...this.state, currentStep: STEP_2 })}
            disabled={isCurrentStep || !this.assessmentSelected() || this.allStepsPassed()}
          >
            <span className={style.scheduleFormStepButtonIcon}>
              {!isCurrentStep && this.accommodationPassed() ? (
                <i className="icon-Check" />
              ) : (
                <i className="icon-Pencil" />
              )}
            </span>
            {isCurrentStep ? (
              <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
            ) : (
              <span className={style.scheduleFormStepTitle}>{title}</span>
            )}
          </button>
        </div>
        <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
          <div className={style.scheduleFormStepWrapper}>
            <div className={style.scheduleFormStep}>
              <div>
                {!user.isGT && accommodationsContent}
                {!user.isGT && accommodation504}
                {user.isGT && accommodationGT}
                {buttons}
              </div>
            </div>
          </div>
        </AnimateHeight>
      </div>
    );
  };

  renderTimeSlots = (timeSlots, location = null) => {
    const { intl } = this.props;
    const { dates, timeSlots: timeSlotsByDates } = this.getTimeSlotsByDates(timeSlots);

    const noLocationsCheck = () => {
      if (this.props.isGT) {
        return (
          <Fragment>
            <p>
              <FormattedMessage {...localMessages.gtNoTimeSlotsCurrentStudents} />
            </p>
            <p>
              <FormattedMessage {...localMessages.gtNoTimeSlotsNotCurrentStudents} />
            </p>
          </Fragment>
        );
      }

      return <FormattedMessage {...localMessages.programTimeSlotsNoLocationTitle} />;
    };

    return (
      <div key={location ? location.dbn : 'no-location'}>
        <fieldset>
          {location ? (
            <Fragment>
              <FormattedMessage {...localMessages.programTimeSlotsLocationTitle} />{' '}
              <b>
                {location.name}, {location.full_address}
              </b>
            </Fragment>
          ) : (
            noLocationsCheck()
          )}
        </fieldset>
        <fieldset
          className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}
        >
          {dates.sort().map(date => (
            <div key={date} className={style.scheduleFormTimeSlotsForDate}>
              <div className={style.scheduleFormTimeSlotDate}>
                <div aria-hidden>
                  <FormattedDate value={date} weekday="long" year="numeric" month="long" day="numeric" timeZone="UTC" />
                </div>
              </div>
              {timeSlotsByDates[date].sort(this.sortTimeSlotByTime).map(timeSlot => {
                const key = `timeSlot-${timeSlot.id}`;
                let helpText;

                const { conflict } = timeSlot;

                if (conflict) {
                  helpText = (
                    <div>
                      <FormattedMessage {...messages.generalStringConflict} />
                      :&nbsp;
                      <FormattedMessage
                        {...localMessages.timeSlotConflict}
                        values={{
                          admissionMethod: conflict.program.admission_method
                            ? conflict.program.admission_method.name
                            : 'Appointment',
                          assessments: conflict.assessment_types.map(at => at.name.toLowerCase()).join(', '),
                          school: conflict.time_slot.school.name,
                        }}
                      />
                    </div>
                  );
                }
                const label = formatTime(timeSlot.start_at);
                const dateFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
                return (
                  <Checkbox
                    id={key}
                    key={key}
                    value={timeSlot.id}
                    disabled={timeSlot.conflict !== null}
                    checked={this.state.selectedTimeSlot === `${timeSlot.id}`}
                    label={label}
                    onChange={this.timeSlotChanged}
                    helpText={helpText}
                    ariaLabel={`${formatDate(date, intl, dateFormatOptions)} ${label}`}
                  />
                );
              })}
            </div>
          ))}
        </fieldset>
      </div>
    );
  };

  renderFormStep3 = (show = false) => {
    const { intl, focusModal, isGT } = this.props;
    const filteredTimeSlots = this.getFilteredTimeSlots();
    const { locations, timeSlots, timeSlotsNoLocation } = this.getTimeSlotsByLocation(filteredTimeSlots);

    let timeSlotsContent = (
      <div className={style.scheduleFormTimeSlotsEmpty}>
        {!this.props.isGT && <FormattedMessage {...localMessages.programNoTimeSlots} />}
        {this.props.isGT && (
          <Fragment>
            <p>
              <FormattedMessage {...localMessages.gtNoTimeSlotsCurrentStudents} />
            </p>
            <p>
              <FormattedMessage {...localMessages.gtNoTimeSlotsNotCurrentStudents} />
            </p>
            <p>
              <FormattedMessage {...localMessages.gtNoTimeSlotsClickNextInstruction} />
            </p>
          </Fragment>
        )}
      </div>
    );

    if (timeSlotsNoLocation.length > 0 || locations.length > 0) {
      timeSlotsContent = (
        <div className={style.scheduleFormTimeSlots}>
          <div className={style.scheduleFormTimeText}>
            <FormattedMessage {...localMessages.programTimeSlotsText} />
          </div>
          {locations.map(location => this.renderTimeSlots(timeSlots[location.dbn], location))}
          {timeSlotsNoLocation.length > 0 && this.renderTimeSlots(timeSlotsNoLocation)}
        </div>
      );
    }

    const scheduleDisabled =
      (locations.length === 0 && timeSlotsNoLocation.length === 0) || this.state.selectedTimeSlot === null;
    const buttons = (
      <div className={style.scheduleFormTimeProgressButtons}>
        <div
          className={classNames(
            style.scheduleFormTimeProgressButtonNext,
            style.scheduleFormProgressButtonWrapper,
            style.scheduleFormProgressButtonWrapperNoTopMargin,
          )}
        >
          {!isGT && (
            <Button
              label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
              onClick={() => {
                this.setState({ ...this.state, currentStep: STEP_4, timeSlotStepPassed: true });
                focusModal();
              }}
              disabled={scheduleDisabled}
              iconColor={colorTeal}
            />
          )}
          {isGT && !locations.length && (
            <Button
              label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
              onClick={() => {
                this.props.flagNoMatchingTimeslot(
                  this.props.user.applicationId,
                  {
                    student_application_id: this.props.applicationId,
                    schedule_id: this.props.program.schedule.programs ? null : this.props.program.schedule.id,
                    shared_schedule_id: this.props.program.schedule.programs ? this.props.program.schedule.id : null,
                    accommodation_large_print: this.state.accommodationLargePrint,
                    accommodation_other: this.state.accommodationOther,
                  },
                  this.state.accommodation504,
                );
                this.setState({ ...this.state, currentStep: NONE_WORK_STEP });
                focusModal();
              }}
              iconColor={colorTeal}
            />
          )}
          {isGT && locations.length > 0 && (
            <Button
              label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
              onClick={() => {
                this.setState({ ...this.state, currentStep: STEP_4, timeSlotStepPassed: true });
                focusModal();
              }}
              disabled={scheduleDisabled}
              iconColor={colorTeal}
            />
          )}
        </div>
        <div
          className={classNames(
            style.scheduleFormTimeProgressButtonNone,
            style.scheduleFormProgressButtonWrapper,
            style.scheduleFormProgressButtonWrapperSecondary,
            style.scheduleFormProgressButtonWrapperNoTopMargin,
          )}
        >
          {(isGT && locations.length > 0) ||
            (!isGT && (
              <Button
                secondary
                onClick={() => {
                  this.props.flagNoMatchingTimeslot(
                    this.props.user.applicationId,
                    {
                      student_application_id: this.props.applicationId,
                      schedule_id: this.props.program.schedule.programs ? null : this.props.program.schedule.id,
                      shared_schedule_id: this.props.program.schedule.programs ? this.props.program.schedule.id : null,
                    },
                    this.state.accommodation504,
                  );
                  this.setState({ ...this.state, currentStep: NONE_WORK_STEP });
                  focusModal();
                }}
                label={customisedIntlFormatMessage(intl, localMessages.programTimeSlotsNoneWork)}
              />
            ))}
        </div>
      </div>
    );

    const { stepButtonClasses } = this.getStepButtonClasses(STEP_3);
    if (this.props.isGT && !this.state.locationStepPassed) {
      stepButtonClasses.push(style.scheduleFormStepButtonDisabled);
    }

    const { selectedTimeSlot } = this.state;
    let title = <FormattedMessage {...localMessages.programTimeSlotsTitleSelected} />;
    let titleMessage = customisedIntlFormatMessage(intl, localMessages.programTimeSlotsTitleSelected);

    const isCurrentStep = this.isCurrentStep(STEP_3);
    const timeSlot = selectedTimeSlot ? filteredTimeSlots.find(ts => `${ts.id}` === selectedTimeSlot) : null;

    if (!isCurrentStep && timeSlot) {
      title = (
        <FormattedMessage
          {...localMessages.programTimeSlotsTitle}
          values={{
            datetime: `${intl.formatDate(timeSlot.start_at)} at ${formatTime(timeSlot.start_at)}`,
            schoolName: timeSlot.school ? timeSlot.school.name : customisedIntlFormatMessage(intl, messages.generalStringNotKnownYet),
            location: timeSlot.school
              ? timeSlot.school.full_address
              : customisedIntlFormatMessage(intl, messages.generalStringNotKnownYet),
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      );
      titleMessage = customisedIntlFormatMessage(intl, localMessages.programTimeSlotsTitle, {
        datetime: `${intl.formatDate(timeSlot.start_at)} at ${formatTime(timeSlot.start_at)}`,
        schoolName: timeSlot.school ? timeSlot.school.name : customisedIntlFormatMessage(intl, messages.generalStringNotKnownYet),
        location: timeSlot.school
          ? timeSlot.school.full_address
          : customisedIntlFormatMessage(intl, messages.generalStringNotKnownYet),
      });
    }

    return (
      <div key="step3" className={style.scheduleFormStepContainer}>
        <div
          className={style.scheduleFormStepTitleContainer}
          id="step3-title-container"
          tabIndex={isCurrentStep ? '0' : '-1'} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
          aria-label={
            isCurrentStep ? `${titleMessage}, ${customisedIntlFormatMessage(intl, messages.generalStringExpandedSection)}` : ''
          }
        >
          <button
            className={classNames(...stepButtonClasses)}
            onClick={() => this.setState({ ...this.state, currentStep: STEP_3 })}
            aria-expanded={isCurrentStep}
            disabled={
              isCurrentStep || !this.assessmentSelected() || !this.accommodationPassed() || this.allStepsPassed()
            }
          >
            <span className={style.scheduleFormStepButtonIcon}>
              {!isCurrentStep && this.timeSlotPassed() && this.timeSlotSelected() ? (
                <i className="icon-Check" />
              ) : (
                <i className="icon-Pencil" />
              )}
            </span>
            {isCurrentStep ? (
              <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
            ) : (
              <span className={style.scheduleFormStepTitle}>{title}</span>
            )}
          </button>
        </div>
        <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
          <div className={style.scheduleFormStepWrapper}>
            <div className={style.scheduleFormStep}>
              {timeSlotsContent}
              {buttons}
            </div>
          </div>
        </AnimateHeight>
      </div>
    );
  };

  renderFormStep4Title = () => {
    const { reSchedule } = this.state;

    if (reSchedule) {
      return (
        <div>
          <div className={style.scheduleFormScheduleTitle}>
            <FormattedMessage {...localMessages.programReScheduleTitle} />
          </div>
          <div className={style.scheduleFormScheduleSubtitle}>
            <FormattedMessage {...localMessages.programReScheduleSubTitle} />
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className={style.scheduleFormScheduleTitle}>
          <FormattedMessage {...localMessages.programScheduleTitle} />
        </div>
        <div className={style.scheduleFormScheduleSubtitle}>
          <FormattedMessage {...localMessages.programScheduleSubTitle} />
        </div>
      </div>
    );
  };

  renderFormStep4 = () => {
    const { appointmentForm, updateAppointmentForm, program, user, intl, focusModal } = this.props;
    const { reSchedule } = this.state;

    let programId = null;
    if (program.directory_program) {
      programId = program.directory_program.id;
    }
    const studentApplications = user.studentApplications.results;

    const filteredTimeSlots = this.getFilteredTimeSlots();
    const { dates } = this.getTimeSlotsByDates(filteredTimeSlots);

    if (reSchedule) {
      const disabled = !this.reScheduleDataChanged() || dates.length === 0;

      return (
        <div>
          <div className={style.scheduleFormReScheduleButtonContainer}>
            <ScheduleButton
              label={customisedIntlFormatMessage(intl, messages.generalStringSubmitEdits)}
              onClick={this.reSchedule}
              disabled={disabled}
            />
            <ScheduleButton
              secondary
              label={customisedIntlFormatMessage(intl, localMessages.appointmentCancelButton)}
              onClick={() => {
                this.setState({
                  ...this.state,
                  currentStep: studentApplications.includes(programId) ? FURTHER_ACTION_STEP : CANCEL_STEP,
                });
                focusModal();
              }}
            />
          </div>
          {disabled ? (
            <div className={style.scheduleFormHelp}>
              <FormattedMessage {...localMessages.programReScheduleUpdateSelection} />
            </div>
          ) : null}
          {updateAppointmentForm.error ? (
            <div className={style.scheduleFormError}>
              <FormattedMessage {...messages.generalStringSomethingWentWrong} />
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <div>
        <div>
          <ScheduleButton
            label={customisedIntlFormatMessage(intl, messages.buttonSubmitRegistration)}
            icon="Check"
            onClick={this.schedule}
          />
        </div>
        {appointmentForm.error ? (
          <div className={style.scheduleFormError}>
            <FormattedMessage {...messages.generalStringSomethingWentWrong} />
          </div>
        ) : null}
      </div>
    );
  };

  renderFormStep5Title = () => (
    <div>
      <div className={style.scheduleFormScheduleTitle}>
        <FormattedMessage {...localMessages.appointmentScheduledTitle} />
      </div>
    </div>
  );

  renderFormStep5 = () => {
    const { isGT } = this.props;
    if (isGT) {
      return (
        <div className={style.scheduleFormScheduledText}>
          <p>
            <FormattedMessage {...localMessages.appointmentGTScheduledTextP1} values={{ b: (chunks) => <b>{chunks}</b> }} />
          </p>
          <p>
            <FormattedMessage {...localMessages.appointmentGTScheduledTextP2}
            values={{
              link: (
                <a href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-and-talented">
                  here
                </a>
              ),
            }}
            />
          </p>
        </div>
      );
    }
    return (
      <div className={style.scheduleFormScheduledText}>
        <FormattedMessage {...localMessages.appointmentScheduledText} values={{ b: (chunks) => <b>{chunks}</b> }} />
      </div>
    );
  };

  renderFormCancelStepTitle = () => (
    <div>
      <div className={style.scheduleFormScheduleTitle}>
        <FormattedMessage {...localMessages.programScheduleCancelTitle} />
      </div>
    </div>
  );

  renderFormCancelStep = () => {
    const { cancelAppointmentForm, intl } = this.props;

    return (
      <div>
        <div className={style.scheduleFormCancelButtonContainer}>
          <ScheduleButton
            label={customisedIntlFormatMessage(intl, messages.buttonYesCancel)}
            icon="Check"
            onClick={this.cancelAppointment}
          />
          <span className={style.scheduleFormCancelButton}>
            <ScheduleButton
              secondary
              label={customisedIntlFormatMessage(intl, messages.buttonNoGoBack)}
              onClick={() => this.setState({ ...this.state, currentStep: STEP_4 })}
            />
          </span>
        </div>
        {cancelAppointmentForm.error ? (
          <div className={style.scheduleFormError}>
            <FormattedMessage {...messages.generalStringSomethingWentWrong} />
          </div>
        ) : null}
      </div>
    );
  };

  renderFurtherActionStepTitle = () => (
    <div>
      <div className={style.scheduleFormScheduleTitle}>
        <FormattedMessage {...messages.programScheduleFurtherActionTitle} />
      </div>
    </div>
  );

  renderFurtherActionStep = () => {
    const { intl, applicationId } = this.props;

    return (
      <div>
        <div
          className={classNames(
            style.scheduleFormScheduleSubtitle,
            style.scheduleFormScheduleSubtitleFurtherAction,
          )}
        >
          <FormattedMessage {...messages.programScheduleFurtherAction} />
        </div>
        <ScheduleButton
          label={customisedIntlFormatMessage(intl, messages.goToApplicationsButton)}
          onClick={() => {
            window.location = `/${intl.locale}/dashboard/${applicationId}/apply`;
          }}
        />
      </div>
    );
  };

  renderNoneWorkStep = () => (
    <div
      className={classNames(
        style.scheduleFormScheduleSubtitle,
        style.scheduleFormScheduleSubtitleNoneWork,
      )}
    >
      {!this.props.isGT && <FormattedMessage {...localMessages.noneWorkText} />}
      {this.props.isGT && <FormattedMessage {...localMessages.gtNoneWorkText} />}
    </div>
  );
  renderFormStepBorough = (show = false) => {
    const { user, intl } = this.props;
    const accommodationGT = (
      <div className={style.scheduleForm504Accommodation}>
        <p>{customisedIntlFormatMessage(intl, localMessages.gtSelectBorough)}</p>
        <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
          <Checkbox
            key="boroughBrooklyn"
            id="boroughBrooklyn"
            value="Brooklyn"
            checked={this.state.filterBorough === 'Brooklyn'}
            label="Brooklyn"
            onChange={this.filterBorough}
          />
          <Checkbox
            key="boroughBronx"
            id="boroughBronx"
            value="Bronx"
            checked={this.state.filterBorough === 'Bronx'}
            label="Bronx"
            onChange={this.filterBorough}
          />
          <Checkbox
            key="boroughManhattan"
            id="boroughManhattan"
            value="Manhattan"
            checked={this.state.filterBorough === 'Manhattan'}
            label="Manhattan"
            onChange={this.filterBorough}
          />
          <Checkbox
            key="boroughQueens"
            id="boroughQueens"
            value="Queens"
            checked={this.state.filterBorough === 'Queens'}
            label="Queens"
            onChange={this.filterBorough}
          />
          <Checkbox
            key="boroughStatenIsland"
            id="boroughStatenIsland"
            value="Staten Island"
            checked={this.state.filterBorough === 'Staten Island'}
            label="Staten Island"
            onChange={this.filterBorough}
          />
        </div>
      </div>
    );

    const { stepButtonClasses } = this.getStepButtonClasses(STEP_BOROUGH);

    if (!this.state.languageStepPassed) {
      stepButtonClasses.push(style.scheduleFormStepButtonDisabled);
    }

    let title = <FormattedMessage {...localMessages.gtSelectBorough} />;

    const isCurrentStep = this.isCurrentStep(STEP_BOROUGH);

    if (!isCurrentStep) {
      if (this.state.filterBorough) {
        title = (
          <span>
            {customisedIntlFormatMessage(intl, localMessages.gtBoroughSelected)} {this.state.filterBorough}
          </span>
        );
      } else {
        title = <FormattedMessage {...localMessages.gtSelectBorough} />;
      }
    }
    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => {
            this.setState({ ...this.state, currentStep: STEP_LOCATION_SCHOOL, boroughStepPassed: true });
            document.getElementById('step3-title-container').focus();
            this.props.getGTfilteredLocations({
              assessment_types: this.state.filterLanguage,
              borough: this.state.filterBorough,
            });
          }}
          disabled={!this.state.filterBorough}
        />
      </div>
    );

    return (
      <div key="stepBorough" className={style.scheduleFormStepContainer}>
        <div className={style.scheduleFormStepTitleContainer}>
          <button
            className={classNames(...stepButtonClasses)}
            aria-expanded={isCurrentStep}
            onClick={() => this.setState({ ...this.state, currentStep: STEP_BOROUGH })}
            disabled={isCurrentStep || !this.state.languageStepPassed || this.allStepsPassed()}
          >
            <span className={style.scheduleFormStepButtonIcon}>
              {!isCurrentStep && this.accommodationPassed() ? (
                <i className="icon-Check" />
              ) : (
                <i className="icon-Pencil" />
              )}
            </span>
            {isCurrentStep ? (
              <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
            ) : (
              <span className={style.scheduleFormStepTitle}>{title}</span>
            )}
          </button>
        </div>
        <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
          <div className={style.scheduleFormStepWrapper}>
            <div className={style.scheduleFormStep}>
              <div>
                {user.isGT && accommodationGT}
                {buttons}
              </div>
            </div>
          </div>
        </AnimateHeight>
      </div>
    );
  };
  renderGTFormStepLanguage = (show = false) => {
    const { intl } = this.props;
    const accommodationGT = (
      <div className={style.scheduleForm504Accommodation}>
        <p>{customisedIntlFormatMessage(intl, localMessages.gtLanguageTitle)}</p>
        <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
          <Checkbox
            key="languageEnglish"
            id="languageEnglish"
            value="English"
            checked={this.state.filterLanguage === 'English'}
            label="English"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageCreole"
            id="languageCreole"
            value="Haitian Creole"
            checked={this.state.filterLanguage === 'Haitian Creole'}
            label="Haitian Creole — Kreyòl ayisyen"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageArabic"
            id="languageArabic"
            value="Arabic"
            checked={this.state.filterLanguage === 'Arabic'}
            label="Arabic — العربية"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageBengali"
            id="languageBengali"
            value="Bengali"
            checked={this.state.filterLanguage === 'Bengali'}
            label="Bengali — বাংলা"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageFrench"
            id="languageFrench"
            value="French"
            checked={this.state.filterLanguage === 'French'}
            label="French — Français"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageSpanish"
            id="languageSpanish"
            value="Spanish"
            checked={this.state.filterLanguage === 'Spanish'}
            label="Spanish — Español"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageChineseMandarin"
            id="languageChineseMandarin"
            value="Chinese - Mandarin"
            checked={this.state.filterLanguage === 'Chinese - Mandarin'}
            label="Chinese (Mandarin) — 中文 (普通话)"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageChineseCantonese"
            id="languageChineseCantonese"
            value="Chinese - Cantonese"
            checked={this.state.filterLanguage === 'Chinese - Cantonese'}
            label="Chinese (Cantonese) — 中文 (广东话)"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageKorean"
            id="languageKorean"
            value="Korean"
            checked={this.state.filterLanguage === 'Korean'}
            label="Korean — 한국어"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageRussian"
            id="languageRussian"
            value="Russian"
            checked={this.state.filterLanguage === 'Russian'}
            label="Russian — Русский"
            onChange={this.filterLanguage}
          />
          <Checkbox
            key="languageUrdu"
            id="languageUrdu"
            value="Urdu"
            checked={this.state.filterLanguage === 'Urdu'}
            label="Urdu — اردو"
            onChange={this.filterLanguage}
          />
        </div>
      </div>
    );
    const { user } = this.props;

    const { stepButtonClasses } = this.getStepButtonClasses(STEP_LANGUAGE);
    if (!this.state.accommodationStepPassed) {
      stepButtonClasses.push(style.scheduleFormStepButtonDisabled);
    }

    let title = <FormattedMessage {...localMessages.gtSelectLanguage} />;

    const isCurrentStep = this.isCurrentStep(STEP_LANGUAGE);

    if (!isCurrentStep) {
      if (this.state.filterLanguage) {
        title = (
          <span>
            {customisedIntlFormatMessage(intl, localMessages.gtLanguageSelected)} {this.state.filterLanguage}
          </span>
        );
      } else {
        title = <FormattedMessage {...localMessages.gtSelectLanguage} />;
      }
    }
    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => {
            this.setState({ ...this.state, currentStep: STEP_BOROUGH, languageStepPassed: true });
          }}
          disabled={!this.state.filterLanguage}
        />
      </div>
    );

    return (
      <div key="stepLanguage" className={style.scheduleFormStepContainer}>
        <div className={style.scheduleFormStepTitleContainer}>
          <button
            className={classNames(...stepButtonClasses)}
            aria-expanded={isCurrentStep}
            onClick={() => this.setState({ ...this.state, currentStep: STEP_LANGUAGE })}
            disabled={isCurrentStep || !this.state.accommodationStepPassed || this.allStepsPassed()}
          >
            <span className={style.scheduleFormStepButtonIcon}>
              {!isCurrentStep && this.accommodationPassed() ? (
                <i className="icon-Check" />
              ) : (
                <i className="icon-Pencil" />
              )}
            </span>
            {isCurrentStep ? (
              <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
            ) : (
              <span className={style.scheduleFormStepTitle}>{title}</span>
            )}
          </button>
        </div>
        <AnimateHeight duration={ANIMATION_DURATION} height={show ? 'auto' : 0} animateOpacity>
          <div className={style.scheduleFormStepWrapper}>
            <div className={style.scheduleFormStep}>
              <div>
                {user.isGT && accommodationGT}
                {buttons}
              </div>
            </div>
          </div>
        </AnimateHeight>
      </div>
    );
  };

  renderGTFormStepLocationSchool = (show = false) => {
    const { intl, locations, user } = this.props;
    const { is_gt_timeslots_eligible: hasGTTimeSlots } = user.details.data;
    let accommodationGT;
    if (!hasGTTimeSlots) {
      accommodationGT = (
        <Fragment>
          <p>
            <FormattedMessage {...localMessages.gtNoTimeSlotsCurrentStudents} />
          </p>
          <p>
            <FormattedMessage {...localMessages.gtNoTimeSlotsNotCurrentStudents} />
          </p>
        </Fragment>
      );
    } else {
      accommodationGT = (
        <div className={style.scheduleForm504Accommodation}>
          <p>Choose Location</p>
          <div className={classNames(style.scheduleFormCheckboxes, style.scheduleFormCheckboxesIndented)}>
            {locations.map(location => (
              <Checkbox
                key={`gtLocation-${location}`}
                id={`gtLocation-${location}`}
                value={location.location_school.dbn}
                schoolName={location.location_school.name}
                label={`${location.location_school.name} - ${location.location_school.full_address}`}
                onChange={this.filterLocation}
                checked={this.state.filterLocation === location.location_school.dbn}
              />
            ))}
          </div>
        </div>
      );
    }

    const { stepButtonClasses } = this.getStepButtonClasses(STEP_LOCATION_SCHOOL);
    if (!this.state.boroughStepPassed) {
      stepButtonClasses.push(style.scheduleFormStepButtonDisabled);
    }

    let title = <FormattedMessage {...localMessages.gtSelectTestingLocation} />;

    const isCurrentStep = this.isCurrentStep(STEP_LOCATION_SCHOOL);

    if (!isCurrentStep) {
      if (this.state.filterLocationName) {
        title = (
          <span>
            {customisedIntlFormatMessage(intl, localMessages.gtTestingLocationSelected)} {this.state.filterLocationName}
          </span>
        );
      } else {
        title = <FormattedMessage {...localMessages.gtSelectTestingLocation} />;
      }
    }

    const buttons = (
      <div className={style.scheduleFormProgressButtonWrapper}>
        <Button
          label={customisedIntlFormatMessage(intl, messages.generalStringNext)}
          onClick={() => {
            this.setState({ ...this.state, currentStep: STEP_3, locationStepPassed: true });
            document.getElementById('step3-title-container').focus();
            this.props.getGTfiltered({
              assessment_types: [this.state.filterLanguage],
              borough: this.state.filterBorough,
              location_school_dbn: this.state.filterLocation,
            });
          }}
          disabled={!this.state.filterLocation && hasGTTimeSlots}
        />
      </div>
    );

    return (
      <div key="stepLocation" className={style.scheduleFormStepContainer}>
        <div className={style.scheduleFormStepTitleContainer}>
          <button
            className={classNames(...stepButtonClasses)}
            aria-expanded={isCurrentStep}
            onClick={() => this.setState({ ...this.state, currentStep: STEP_LOCATION_SCHOOL })}
            disabled={isCurrentStep || !this.state.boroughStepPassed || this.allStepsPassed()}
          >
            <span className={style.scheduleFormStepButtonIcon}>
              {!isCurrentStep && this.accommodationPassed() ? (
                <i className="icon-Check" />
              ) : (
                <i className="icon-Pencil" />
              )}
            </span>
            {isCurrentStep ? (
              <h2 className={style.scheduleFormStepTitleActive}>{title}</h2>
            ) : (
              <span className={style.scheduleFormStepTitle}>{title}</span>
            )}
          </button>
        </div>
        {isCurrentStep && this.checkLocationsLoaded({ accommodationGT, buttons, show })}
      </div>
    );
  };

  getSteps = () => {
    const { user } = this.props;
    const { is_gt_timeslots_eligible: hasGTTimeSlots } = user.details.data;

    if (user.isGT) {
      if (hasGTTimeSlots) {
        return this.stepMappingGTTimeSlots;
      }
      return this.stepMappingGTNoTimeSlots;
    }
    if(user.isLG){
      return this.stepMappingLGA;
    }
    return this.stepMapping;
  };

  render() {
    const {
      appointmentForm,
      cancelAppointmentForm,
      updateAppointmentForm,
      gtFilteredLoading,
      gtFilteredLocationsLoading,
    } = this.props;
    const { currentStep } = this.state;

    const steps = this.getSteps();

    const title = Object.keys(this.stepTitleMapping).includes(currentStep)
      ? this.stepTitleMapping[currentStep]()
      : null;

    return (
      <div className={style.scheduleFormContainer}>
        {gtFilteredLocationsLoading ||
        gtFilteredLoading ||
        appointmentForm.submitting ||
        cancelAppointmentForm.submitting ||
        updateAppointmentForm.submitting ? (
          <Loader />
        ) : (
          <div>
            <div className={style.scheduleFormScheduleTitleContainer}>{title}</div>
            {currentStep !== NONE_WORK_STEP && currentStep !== FURTHER_ACTION_STEP && (
              <div>{Object.keys(steps).map(step => steps[step](step === currentStep))}</div>
            )}
            {currentStep === STEP_4 && <div>{this.renderFormStep4()}</div>}
            {currentStep === STEP_5 && <div>{this.renderFormStep5()}</div>}
            {currentStep === CANCEL_STEP && <div>{this.renderFormCancelStep()}</div>}
            {currentStep === FURTHER_ACTION_STEP && <div>{this.renderFurtherActionStep()}</div>}
            {currentStep === NONE_WORK_STEP && <div>{this.renderNoneWorkStep()}</div>}
          </div>
        )}
      </div>
    );
  }
}

ScheduleForm.defaultProps = {
  focusModal: () => {},
};

ScheduleForm.propTypes = {
  user: PropTypes.shape().isRequired,
  applicationId: PropTypes.string.isRequired,
  program: PropTypes.shape().isRequired,
  studentAppointments: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  skipAssessments: PropTypes.bool.isRequired,
  appointmentForm: PropTypes.shape().isRequired,
  cancelAppointmentForm: PropTypes.shape().isRequired,
  updateAppointmentForm: PropTypes.shape().isRequired,
  makeAppointment: PropTypes.func.isRequired,
  cancelAppointment: PropTypes.func.isRequired,
  updateAppointment: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string,
    messages: PropTypes.shape({}),
    formatMessage: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  focusModal: PropTypes.func,
  flagNoMatchingTimeslot: PropTypes.func.isRequired,
  studentInfo: PropTypes.func.isRequired,
  getGTfiltered: PropTypes.func.isRequired,
  getGTfilteredLocations: PropTypes.func.isRequired,
  isGT: PropTypes.bool.isRequired,
  gtFilteredLocationsLoaded: PropTypes.bool.isRequired,
  gtFilteredLocationsLoading: PropTypes.bool.isRequired,
  gtFilteredLoading: PropTypes.bool.isRequired,
  locations: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
};

const mapState = state => ({
  appointmentForm: state.appointmentForm,
  cancelAppointmentForm: state.cancelAppointmentForm,
  updateAppointmentForm: state.updateAppointmentForm,
});

const mapDispatch = {
  makeAppointment,
  cancelAppointment,
  updateAppointment,
  flagNoMatchingTimeslot,
  studentInfo,
};

export default withRtl(style)(
  injectIntl(
    connect(
      mapState,
      mapDispatch,
    )(withStyles(style)(ScheduleForm)),
  ),
);
